<script setup>
/**
 * CartPromoGroupCard Component
 * Container card for promo items when promo max is exceeded.
 * Shows promo header, small item cards inside, and unified boundary item at bottom.
 */
import CartLineRow from './CartLineRow.vue'

const props = defineProps({
  block: {
    type: Object,
    required: true,
    // Expected shape: { type: 'promoGroup', promoId, promoMeta, groups, boundaryGroup, displayKey }
  },
})

const emit = defineEmits(['inc', 'dec', 'set-qty', 'remove'])

// Forward events from child CartLineRow components
function handleInc(makat) {
  emit('inc', makat)
}

function handleDec(makat) {
  emit('dec', makat)
}

function handleSetQty(makat, qty) {
  emit('set-qty', makat, qty)
}

function handleRemove(makat) {
  emit('remove', makat)
}
</script>

<template>
  <div class="promo-group-card">
    <!-- Promo Header -->
    <div class="promo-header">
      <div class="promo-header-content">
        <span class="promo-badge">
          <svg
            viewBox="0 0 24 24"
            width="14"
            height="14"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
          >
            <path
              d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z"
            />
          </svg>
        </span>
        <span class="promo-label">{{ block.promoMeta?.label || 'מבצע' }}</span>
      </div>
      <div v-if="block.promoMeta?.description" class="promo-description">
        {{ block.promoMeta.description }}
      </div>
      <div v-if="block.promoMeta?.max" class="promo-meta-row">
        <span class="promo-meta-item">מקס: {{ block.promoMeta.max }} יח'</span>
      </div>
    </div>

    <!-- Items in Promo (small cards) -->
    <div v-if="block.groups && block.groups.length > 0" class="promo-items">
      <CartLineRow
        v-for="group in block.groups"
        :key="group.displayKey"
        :group="group"
        variant="embedded"
        @inc="handleInc"
        @dec="handleDec"
        @set-qty="handleSetQty"
        @remove="handleRemove"
      />
    </div>

    <!-- Boundary Item (unified over-max card) -->
    <div v-if="block.boundaryGroup" class="promo-boundary">
      <div class="boundary-label">
        <svg
          viewBox="0 0 24 24"
          width="14"
          height="14"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
        >
          <circle cx="12" cy="12" r="10" />
          <line x1="12" y1="8" x2="12" y2="12" />
          <line x1="12" y1="16" x2="12.01" y2="16" />
        </svg>
        <span>חלק מהכמות חורג מהמקסימום</span>
      </div>
      <CartLineRow
        :group="block.boundaryGroup"
        variant="embedded"
        @inc="handleInc"
        @dec="handleDec"
        @set-qty="handleSetQty"
        @remove="handleRemove"
      />
    </div>
  </div>
</template>

<style scoped>
.promo-group-card {
  display: flex;
  flex-direction: column;
  background: var(--bg-primary);
  border-radius: var(--border-radius);
  border: 2px solid rgba(34, 197, 94, 0.3);
  overflow: hidden;
  transition: all var(--transition-fast);
}

.promo-group-card:hover {
  border-color: rgba(34, 197, 94, 0.5);
  box-shadow: 0 2px 8px rgba(34, 197, 94, 0.1);
}

/* Promo Header */
.promo-header {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xs);
  padding: var(--spacing-sm) var(--spacing-md);
  background: linear-gradient(135deg, rgba(34, 197, 94, 0.12) 0%, rgba(22, 163, 74, 0.08) 100%);
  border-bottom: 1px solid rgba(34, 197, 94, 0.2);
}

.promo-header-content {
  display: flex;
  align-items: center;
  gap: var(--spacing-xs);
}

.promo-badge {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  background: linear-gradient(135deg, #22c55e 0%, #16a34a 100%);
  border-radius: var(--border-radius-full);
  color: white;
  flex-shrink: 0;
}

.promo-label {
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-bold);
  color: #15803d;
}

.promo-description {
  font-size: var(--font-size-xs);
  color: var(--text-secondary);
  line-height: 1.4;
}

.promo-meta-row {
  display: flex;
  align-items: center;
  gap: var(--spacing-md);
  margin-top: var(--spacing-xs);
}

.promo-meta-item {
  font-size: 11px;
  color: var(--text-muted);
  background: rgba(34, 197, 94, 0.1);
  padding: 2px 8px;
  border-radius: var(--border-radius-sm);
}

/* Promo Items (small cards) */
.promo-items {
  display: flex;
  flex-direction: column;
}

/* Boundary Section */
.promo-boundary {
  display: flex;
  flex-direction: column;
  border-top: 1px dashed rgba(245, 158, 11, 0.4);
  background: rgba(245, 158, 11, 0.03);
}

.boundary-label {
  display: flex;
  align-items: center;
  gap: var(--spacing-xs);
  padding: var(--spacing-xs) var(--spacing-md);
  font-size: 11px;
  color: #92400e;
  background: rgba(245, 158, 11, 0.1);
}

.boundary-label svg {
  color: #f59e0b;
  flex-shrink: 0;
}

/* Mobile */
@media (max-width: 767px) {
  .promo-header {
    padding: var(--spacing-xs) var(--spacing-sm);
  }

  .promo-label {
    font-size: var(--font-size-sm);
  }
}
</style>
